import { ContactList, MissionGroupColor, MissionGroupIcon, ShareContact } from "@nova/schemas"
import { createSelector, createSlice } from "@reduxjs/toolkit"
import { fetchExtendedWorkspace } from "features/data-management/workspace/WorkspaceManagementSlice"
import { RootState } from "integrations/redux"
import { memoize } from "nodash"

type MissionGroupsSlice = {
    hasSetContactsForWorkspaceId: string | undefined
    contactListsById: Record<string, ContactList>
    activeGroupId: string | undefined
    changedListIds: string[]
    deletedListIds: string[]
    isSavingChanges: boolean
}

const initialState: MissionGroupsSlice = {
    hasSetContactsForWorkspaceId: undefined,
    contactListsById: {},
    activeGroupId: undefined,
    changedListIds: [],
    deletedListIds: [],
    isSavingChanges: false,
}

export const missionGroupsSliceName = "mission-groups" as const

function doesMissionGroupContainContact(missionGroup: ContactList, contact: ShareContact) {
    return !!missionGroup.contacts.find((c) => {
        return areContactsEqual(c, contact)
    })
}

function areContactsEqual(c1: ShareContact, c2: ShareContact) {
    if (c1.type === "email" && c2.type === "email") return c1.email === c2.email
    if (c1.type === "phone" && c2.type === "phone") return c1.phoneNumber === c2.phoneNumber
    return false
}

function getMissionGroupArray(groupsById: Record<string, ContactList>) {
    return Object.values(groupsById).toSorted((a, b) => a.name.localeCompare(b.name))
}

export const {
    actions: {
        setContactLists,
        setActiveMissionGroupId,
        renameContactList,
        addContactToList,
        removeContactFromList,
        deleteList,
        pushNewList,
        replaceList,
        updateMissionGroupIcon,
        updateMissionGroupAutoNotify,
        updatePasswordEnabled,
        updateMissionGroupPassword,
        setIsSavingChanges,
        clearLocalChanges,
    },
    reducer: MissionGroupsSliceReducer,
} = createSlice({
    name: missionGroupsSliceName,
    initialState,
    reducers: {
        setContactLists: (
            state,
            action: { payload: { contactLists: ContactList[]; workspaceId: string } }
        ) => {
            const { contactLists, workspaceId } = action.payload
            state.hasSetContactsForWorkspaceId = workspaceId
            state.contactListsById = contactLists.reduce(
                (acc, cur) => {
                    acc[cur.id] = cur
                    return acc
                },
                {} as Record<string, ContactList>
            )
            ;((state.changedListIds = []), (state.deletedListIds = []))
            const missionGroupsArray = getMissionGroupArray(state.contactListsById)
            if (
                !state.activeGroupId ||
                !missionGroupsArray.some((group) => group.id === state.activeGroupId)
            ) {
                state.activeGroupId = getMissionGroupArray(state.contactListsById)[0]?.id
            }
        },
        setActiveMissionGroupId: (state, action: { payload: { groupId: string } }) => {
            state.activeGroupId = action.payload.groupId
        },
        renameContactList: (
            state,
            action: { payload: { contactListId: string; newName: string } }
        ) => {
            const { contactListId, newName } = action.payload
            if (!state.contactListsById[contactListId]) {
                console.warn("Renaming list that doesn't exist")
                return
            }
            state.contactListsById[contactListId].name = newName
            if (!state.changedListIds.includes(contactListId)) {
                state.changedListIds.push(contactListId)
            }
        },
        addContactToList: (
            state,
            action: { payload: { contactListId: string; contact: ShareContact } }
        ) => {
            const { contactListId, contact } = action.payload
            if (!state.contactListsById[contactListId]) {
                console.warn("Adding a contact to a list that doesn't exist")
                return
            }
            if (doesMissionGroupContainContact(state.contactListsById[contactListId], contact)) {
                console.warn("Adding a contact to a list where it already exists")
                return
            }
            state.contactListsById[contactListId].contacts.push(contact)
            if (!state.changedListIds.includes(contactListId)) {
                state.changedListIds.push(contactListId)
            }
        },
        removeContactFromList: (
            state,
            action: { payload: { contactListId: string; contact: ShareContact } }
        ) => {
            const { contactListId, contact } = action.payload
            if (!state.contactListsById[contactListId]) {
                console.warn("Deleting contact from a list that doesn't exist")
                return
            }
            if (!doesMissionGroupContainContact(state.contactListsById[contactListId], contact)) {
                console.warn("Deleting contact that doesn't exist")
                return
            }
            state.contactListsById[contactListId].contacts = state.contactListsById[
                contactListId
            ].contacts.filter((c) => !areContactsEqual(c, contact))
            if (!state.changedListIds.includes(contactListId)) {
                state.changedListIds.push(contactListId)
            }
        },
        deleteList: (state, action: { payload: { contactListId: string } }) => {
            const { contactListId } = action.payload
            if (!state.contactListsById[contactListId]) {
                console.warn("Deleting contact list that doesn't exist")
                return
            }
            delete state.contactListsById[contactListId]
            state.deletedListIds.push(contactListId)
            state.activeGroupId = getMissionGroupArray(state.contactListsById)[0]?.id
        },
        pushNewList: (state, action: { payload: ContactList }) => {
            const list = action.payload
            if (state.contactListsById[list.id]) {
                console.error("Created new contact list with existing id")
                return
            }
            state.contactListsById[list.id] = list
        },
        replaceList: (state, action: { payload: ContactList }) => {
            const list = action.payload
            if (!state.contactListsById[list.id]) return
            state.contactListsById[list.id] = list
        },
        updateMissionGroupIcon: (
            state,
            action: {
                payload: {
                    groupId: string
                    icon: MissionGroupIcon
                    color: MissionGroupColor
                }
            }
        ) => {
            const { groupId, icon, color } = action.payload
            if (!state.contactListsById[groupId]) return
            state.contactListsById[groupId].icon = icon
            state.contactListsById[groupId].color = color
            if (!state.changedListIds.includes(groupId)) {
                state.changedListIds.push(groupId)
            }
        },
        updateMissionGroupAutoNotify: (
            state,
            action: { payload: { groupId: string; autoNotify: boolean } }
        ) => {
            const { groupId, autoNotify } = action.payload
            if (!state.contactListsById[groupId]) return
            state.contactListsById[groupId].autoNotify = autoNotify
            if (!state.changedListIds.includes(groupId)) {
                state.changedListIds.push(groupId)
            }
        },
        updatePasswordEnabled: (
            state,
            action: { payload: { groupId: string; passwordEnabled: boolean } }
        ) => {
            const { groupId, passwordEnabled } = action.payload
            if (!state.contactListsById[groupId]) return
            state.contactListsById[groupId].passwordEnabled = passwordEnabled
            if (!passwordEnabled) {
                state.contactListsById[groupId].password = undefined
            }
            if (!state.changedListIds.includes(groupId)) {
                state.changedListIds.push(groupId)
            }
        },
        updateMissionGroupPassword: (
            state,
            action: { payload: { groupId: string; password: string | undefined } }
        ) => {
            const { groupId, password } = action.payload
            if (!state.contactListsById[groupId]) return
            state.contactListsById[groupId].password = password
            if (!state.changedListIds.includes(groupId)) {
                state.changedListIds.push(groupId)
            }
        },
        setIsSavingChanges: (state, action: { payload: boolean }) => {
            state.isSavingChanges = action.payload
        },
        clearLocalChanges: (state) => {
            state.changedListIds = []
            state.deletedListIds = []
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchExtendedWorkspace.pending, (state) => {
            state.contactListsById = {}
            state.activeGroupId = undefined
            state.changedListIds = []
            state.deletedListIds = []
            state.isSavingChanges = false
        })
    },
})

const root = (state: RootState) => state[missionGroupsSliceName]

export const selectContactListsArray = createSelector([root], (s) => {
    return getMissionGroupArray(s.contactListsById)
})
export const selectContactListById = memoize((listId: string) =>
    createSelector([root], (s) => s.contactListsById[listId] as ContactList | undefined)
)

export const selectActiveMissionGroupId = createSelector([root], (s) => s.activeGroupId)
export const selectActiveMissionGroup = createSelector(
    [selectActiveMissionGroupId, selectContactListsArray],
    (activeId, groups) => {
        return groups.find((group) => group.id === activeId)
    }
)

export const selectAnyListHasChanges = createSelector(
    [root],
    (s) => !!s.changedListIds.length || !!s.deletedListIds.length
)
export const selectChangedListIds = createSelector([root], (s) => s.changedListIds)
export const selectDoesListHaveChanges = memoize((listId: string) =>
    createSelector([selectChangedListIds], (lists) => lists.includes(listId))
)
export const selectDeletedListIds = createSelector([root], (s) => s.deletedListIds)

export const selectIsSavingChanges = createSelector([root], (s) => s.isSavingChanges)
export const selectHasSetContactsForWorkspaceId = createSelector(
    [root],
    (s) => s.hasSetContactsForWorkspaceId
)
